<template>
  <div>
    <vue-headful :title="this.setPageTitle('efficiencyTests')" />
    <loading :active="loadingDetails || loadingInsights" :color="'blue'" is-full-page loader="spinner"> </loading>
    <b-container class="my-4">
      <!-- TITLE -->
      <b-row class="mb-4">
        <b-col>
          <div class="d-flex align-items-center">
            <span class="mr-2">
              <a
                @click.prevent="
                  $router.push({
                    name: 'efficiencyTestsList',
                  })
                "
                class="pointer"
              >
                <span class="mr-2 align-middle"><i class="fas fa-chevron-left fa-lg"></i></span></a
            ></span>
            <div class="mr-auto">
              <h1 class="h5 mb-0">
                {{ getScoringProfileName }}
              </h1>
            </div>
            <!-- Score and outcome chip, aligned with the test name but on the opposite (right) side. -->
            <span v-if="hasTestedProductScore" class="mr-3 text-nowrap" data-test="testedProductScore">
              {{ $t('efficiencyTestDetails.score') }}: <strong>{{ $n(getTestedProductScore, 'percent') }}</strong>
            </span>
            <efficiency-test-outcome :outcome="getSummary.outcome"></efficiency-test-outcome>
          </div>
        </b-col>
      </b-row>

      <hr />

      <b-row>
        <b-col class="scoring-descriptions mb-3">
          {{ getScoringProfileDescription }}
        </b-col>
      </b-row>

      <b-row v-if="!loadingDetails" class="mt-2">
        <b-col>
          <profile-features :data-coverage="getCoverageFiltered" :test-id="scoringProfileId"></profile-features>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <div class="float-right my-3 d-flex align-items-start">
            <action-button
              v-if="getExportPermission"
              :options="exportButtonOptions"
              :is-disabled="isDownloading || isGeneratingReport"
              :title="exportButtonTitle"
            ></action-button>
            <action-button
              class="ml-2"
              :options="chartButtonOptions"
              :title="$t('efficiencyTests.chartSelect.label')"
              data-test="chartSelect"
            ></action-button>
          </div>
        </b-col>
      </b-row>

      <!-- All the views are stacked in the same grid cell: the container keeps the tallest one's height,
           so switching chart never resizes the page. -->
      <b-row>
        <b-col>
          <div class="charts-stack">
            <div :class="chartStackClass(CHARTS.distribution)" :aria-hidden="selectedChart !== CHARTS.distribution">
              <insight
                :insightsProducts="getInsightProducts"
                @show-distribution-score="showDistributionScoreData"
              ></insight>
            </div>
            <div :class="chartStackClass(CHARTS.bubble)" :aria-hidden="selectedChart !== CHARTS.bubble">
              <drivers-bubble-chart
                v-if="!loadingInsights"
                :drivers="getSummary.drivers || []"
                :tested-product="getTestedProduct"
                :comparables="getComparables"
              ></drivers-bubble-chart>
            </div>
            <div :class="chartStackClass(CHARTS.boxPlot)" :aria-hidden="selectedChart !== CHARTS.boxPlot">
              <drivers-box-plot-chart
                v-if="!loadingInsights"
                :drivers="getSummary.drivers || []"
                :tested-product="getTestedProduct"
                :comparables="getComparables"
              ></drivers-box-plot-chart>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row v-if="!loadingInsights">
        <b-col class="mb-4">
          <scoring-coverage-filters
            :issuerOptions="getCoverageIssuersList"
            :hasFiltersApplied="someCoverageFiltersApplied"
            :countFiltersSelected="countCoverageFiltersApplied"
            :issuersFilter="coverageFilters.issuers"
            :sliderScoreF="coverageFilters.scoreF"
            :sliderScoreT="coverageFilters.scoreT"
            :compatibilityFilter="coverageFilters.compatibility"
            :searchBar="searchBar"
            is-profile-editing
            @reset-coverage-filters="resetFilters"
            @update:searchBar="filterByString"
            @on-apply-filters="onApplyFilters"
          ></scoring-coverage-filters>
        </b-col>
      </b-row>

      <b-row v-if="!loadingInsights">
        <b-col v-if="!productsFound">
          <no-content
            :icon-name="'exclamation-circle'"
            :icon-class="['mb-3', 'fa-3x']"
            :text="$t('common.noScoringProfilesFound')"
          ></no-content>
        </b-col>
        <b-col v-else>

          <insights-table
            :tableItems="getFilteredProducts"
            @click-table-row="onClickTableRow"
            @show-distribution-score="showDistributionScoreData"
            @show-product-score-data="showProductScoreData"
            @show-incompatible-score-filters="showIncompatibleScoreFilters"
          />
        </b-col>
      </b-row>
      <custom-modal
        v-if="insightsModalValue != null"
        :id="'insightsDetails'"
        v-model="insightsModalValue"
        @on-click-table-row="onClickTableRow"
      />
      <incompatible-filters-modal
        v-if="_incompatibleFiltersValue != null"
        :filters-data="_incompatibleFiltersValue"
        modal-id="incompatibleFilters"
      />
    </b-container>
  </div>
</template>

<script>
import ProfileFeatures from '../../components/EfficiencyTests/ProfileFeatures.vue'
import Insight from '../../components/EfficiencyTests/Insight.vue'
import ScoringCoverageFilters from '../../components/EfficiencyTests/ScoringCoverageFilters.vue'
import InsightsTable from '../../components/EfficiencyTests/InsightsTable.vue'
import NoContent from '../../components/Lib/NoContent'
import CustomModal from '../../components/Lib/CustomModal'
import IncompatibleFiltersModal from '../../components/EfficiencyTests/IncompatibleFiltersModal.vue'
import { createNamespacedHelpers } from 'vuex'
import { safeGet } from '../../utils/safeGet'
import { mathRoundPercentage, round } from '../../utils/formatter'
import ActionButton from '../../components/Lib/ActionButton.vue'
import {
  FILE_NAME_EXPORT_EFFICIENCY_TEST,
  FILE_NAME_REPORT_EFFICIENCY_TEST,
  INIT_COVERAGE_FILTERS_INSIGHTS,
} from '../../settings/scoringProfilesSettings'

import { getFile } from '../../utils/fetcher'
import { deepDiffMapper, VALUE_CHANGED } from '../../utils/objDifference'
import EfficiencyTestOutcome from '@/components/EfficiencyTests/EfficiencyTestOutcome.vue'
import DriversBubbleChart from '@/components/EfficiencyTests/DriversBubbleChart.vue'
import DriversBoxPlotChart from '@/components/EfficiencyTests/DriversBoxPlotChart.vue'

const CHARTS = {
  distribution: 'distribution',
  bubble: 'bubble',
  boxPlot: 'boxPlot',
}

const { mapGetters, mapActions } = createNamespacedHelpers('efficiencyTestsDetails')

export default {
  components: {
    EfficiencyTestOutcome,
    DriversBubbleChart,
    DriversBoxPlotChart,
    Insight,
    ScoringCoverageFilters,
    InsightsTable,
    ProfileFeatures,
    CustomModal,
    ActionButton,
    NoContent,
    IncompatibleFiltersModal,
  },
  props: {
    scoringProfileId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loadingDetails: false,
      loadingInsights: false,
      isDownloading: false,
      isGeneratingReport: false,
      insightsModalValue: null,
      incompatibleFiltersValue: null,
      coverageFilters: INIT_COVERAGE_FILTERS_INSIGHTS(),
      searchBar: '',
      CHARTS,
      selectedChart: CHARTS.distribution,
    }
  },
  computed: {
    ...mapGetters([
      'getCoverage',
      'getTestedProduct',
      'getComparables',
      'getInsightProducts',
      'getExportPermission',
      'getCoverageIssuersList',
      'getFilteredProducts',
      'getSummary',
      'getTestedProductScore'
    ]),

    hasTestedProductScore() {
      return typeof this.getTestedProductScore === 'number' && !isNaN(this.getTestedProductScore)
    },

    chartButtonOptions() {
      // Same ActionButton (b-dropdown) as "Esporta punteggi" so the two controls look identical;
      // each option just switches the visible chart.
      return [
        {
          label: this.$t('efficiencyTests.chartSelect.distribution'),
          callback: () => {
            this.selectedChart = CHARTS.distribution
          },
          variant: 'primary',
        },
        {
          label: this.$t('efficiencyTests.chartSelect.bubble'),
          callback: () => {
            this.selectedChart = CHARTS.bubble
          },
          variant: 'primary',
        },
        {
          label: this.$t('efficiencyTests.chartSelect.boxPlot'),
          callback: () => {
            this.selectedChart = CHARTS.boxPlot
          },
          variant: 'primary',
        },
      ]
    },
    productsFound() {
      return this.getFilteredProducts.length > 0
    },
    // Now shown via ProfileFeatures' summary section instead (comparablesCount breakdown /
    // criteriaUsedCount) — kept out of the coverage strip to avoid showing the same numbers twice.
    getCoverageFiltered() {
      const excluded = [
        'excludedProductsCount',
        'includedProductsCount',
        'primaryMarketComparablesCount',
        'secondaryMarketComparablesCount',
      ]
      return Object.fromEntries(Object.entries(this.getCoverage).filter(([key]) => !excluded.includes(key)))
    },
    getScoringProfileName() {
      return this.$store.getters['efficiencyTestsCommon/getScoringProfileName']
    },
    getScoringProfileDescription() {
      return this.$store.getters['efficiencyTestsCommon/getScoringProfileDescription']
    },
    // Simplified from the old ScoringProfiles version: there's no MOP/investment-option level here,
    // every plotted point is a whole product (see getInsightProducts).
    distributionModalFields() {
      return [
        { key: 'issuerName', label: this.$t('search.resultTable.issuer'), sortable: true },
        { key: 'name', label: this.$t('common.product'), sortable: true },
        {
          key: 'scoreResult',
          label: this.$t('common.scoring'),
          sortable: true,
          class: ['text-right'],
          formatter: (val) => (typeof val === 'number' && !isNaN(val) ? mathRoundPercentage(val) : '-'),
        },
      ]
    },
    productScoreModalFields() {
      return [
        { key: 'investmentOptionName', label: this.$t('common.option'), sortable: true },
        { key: 'compatibilityType', label: this.$t('common.compatibility'), sortable: true },
        {
          key: 'totalScore',
          label: this.$t('common.scoring'),
          sortable: true,
          class: ['text-right'],
          formatter: (val) => (typeof val === 'number' && !isNaN(val) ? mathRoundPercentage(val) : '-'),
        },
      ]
    },
    _incompatibleFiltersValue: {
      get() {
        return this.incompatibleFiltersValue
      },
      set(newValue) {
        this.incompatibleFiltersValue = newValue
      },
    },
    // Single "Esporta" dropdown with both exports. Labels are STABLE (not state-dependent): the
    // in-progress feedback is driven by exportButtonTitle + is-disabled instead, so ActionButton's
    // "reset selectedOption to options[0] whenever the options array changes" watcher can't mislabel
    // the running action. Both items share getExportPermission (gated on the element in the template).
    exportButtonOptions() {
      return [
        {
          label: this.$t('efficiencyTests.exportMenu.scores'),
          callback: () => this.exportData(),
          variant: 'primary',
        },
        {
          label: this.$t('efficiencyTests.exportMenu.report'),
          callback: () => this.generateReport(),
          variant: 'primary',
        },
      ]
    },
    // Button label: "Esporta" at rest, a per-action "in corso" message while one of the two runs.
    exportButtonTitle() {
      if (this.isDownloading) return this.$t('search.label.exportResultsDownloading')
      if (this.isGeneratingReport) return this.$t('efficiencyTests.generatingReport')
      return this.$t('efficiencyTests.export')
    },
    someCoverageFiltersApplied() {
      return this.countCoverageFiltersApplied > 0
    },
    countCoverageFiltersApplied() {
      const defaultFilters = INIT_COVERAGE_FILTERS_INSIGHTS()
      const appliedFilters = this.coverageFilters
      const diff = deepDiffMapper(appliedFilters, defaultFilters).filterByType(VALUE_CHANGED).result
      //any field pairs as xxxF and xxxT count as 1
      const values = Object.keys(diff).filter(
        (key) => !(key.endsWith('F') && Object.keys(diff).includes(key.slice(0, -1) + 'T'))
      )

      let filtersApplied = values.length

      if (this.searchBar !== '') {
        filtersApplied = filtersApplied + 1
      }

      return filtersApplied
    },
  },
  methods: {
    ...mapActions(['loadInsight', 'filteredProducts']),
    chartStackClass(chart) {
      return ['charts-stack-item', { 'charts-stack-hidden': this.selectedChart !== chart }]
    },
    onClickTableRow(value) {
      this.openProductDetails(value, this.scoringProfileId, true)
    },
    // `item` is only present when triggered from a single product's own embedded chart inside
    // InsightsTable's row-details (fairmat-product-score's show-distribution) — Insight.vue's
    // aggregate chart calls this with just `value`. In the per-item case the elements to show come
    // straight from the third-party component's own event payload (value.elements), same as the
    // legacy ScoringProfiles page did; there is no per-item data of ours to recompute here.
    showDistributionScoreData(value, item) {
      const obj =
        item != null ? { title: item.name, subtitle: '' } : { title: this.getScoringProfileName, subtitle: '' }

      const items = this.distributionScoreItems(value, item)

      this.insightsModalValue = {
        title: obj.title,
        subtitle: obj.subtitle,
        subHeader: {
          number: items.length,
          label: this.$t('common.modal.elementsInRange'),
        },
        otherInfo: {
          label1:
            this.$t('common.modal.rangeFilter') +
            ' ' +
            mathRoundPercentage(value.min) +
            ' - ' +
            mathRoundPercentage(value.max),
        },
        placeholder: this.$t('common.searchInvestmentOption'),
        items: items,
        fields: this.distributionModalFields,
        labelDisplayClass: ['d-inline'],
      }

      this.$nextTick(() => {
        this.$bvModal.show('insightsDetails')
      })
    },
    distributionScoreItems(value, item) {
      const elements =
        item != null
          ? value.elements || []
          : [...this.getInsightProducts.productsMarketSegment, ...this.getInsightProducts.productsUnderTesting]

      const scoreKey = item != null ? 'totalScore' : 'score'

      let temp = elements.filter((el) => el[scoreKey] != null && round(el[scoreKey], 8) >= round(value.min, 8))

      if (!value.isLastColumns) {
        temp = temp.filter((el) => round(el[scoreKey], 8) < round(value.max, 8))
      }

      return temp.map((el) => ({
        id: el.productId,
        name: el.name || el.investmentOptionName,
        issuerName: el.issuerName,
        issuerCode: el.issuerCode,
        referenceDate: el.referenceDate,
        scoreResult: el[scoreKey],
      }))
    },
    // Also fairmat-product-score's own event, for its "show full options list" action button.
    showProductScoreData(value, item) {
      const obj =
        item != null
          ? { title: item.name, subtitle: safeGet(() => item.productScore.scoringProfileName, '') || '' }
          : { title: this.getScoringProfileName, subtitle: '' }

      this.insightsModalValue = {
        title: obj.title,
        subtitle: obj.subtitle,
        subHeader: {
          number: value.scoreResults.length,
          label: this.$t('common.options'),
        },
        otherInfo: {
          label1:
            value.scoreResults.filter((el) => el.compatibilityType === 1).length +
            ' ' +
            this.$t('common.compatibleOptions') +
            ', ' +
            (value.scoreResults.filter((el) => el.compatibilityType === 0).length +
              ' ' +
              this.$t('common.notCompatibleOptions')),
        },
        placeholder: this.$t('common.searchInvestmentOption'),
        items: this.productScoreItems(value),
        fields: this.productScoreModalFields,
        labelDisplayClass: ['d-inline'],
      }

      this.$nextTick(() => {
        this.$bvModal.show('insightsDetails')
      })
    },
    productScoreItems(value) {
      if (!value) return []

      return value.scoreResults.map((item) => {
        return {
          name: safeGet(() => value.productKey.name, ''),
          issuerCode: safeGet(() => value.productKey.issuerCode, ''),
          referenceDate: safeGet(() => value.productKey.referenceDate, null),
          investmentOptionName: item.investmentOptionName,
          compatibilityType:
            item.compatibilityType === 1 ? this.$t('common.compatible') : this.$t('common.notCompatible'),
          totalScore: item.totalScore,
        }
      })
    },
    showIncompatibleScoreFilters(value) {
      this._incompatibleFiltersValue = value

      this.$nextTick(() => {
        this.$bvModal.show('incompatibleFilters')
      })
    },
    // The scores and the report endpoints serve the .xlsx as is, not as the base64 wrapper of the search exports, hence the
    // blob download. `loadingFlag` is the data property that says the download is under way.
    downloadWorkbook(endpoint, params, loadingFlag, fallbackFileName) {
      this[loadingFlag] = true
      getFile(endpoint, params)
        .then(({ blob, filename }) => {
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = filename || fallbackFileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(link.href)
        })
        .catch(() => alert(this.$t('common.errorReceivingData')))
        .finally(() => {
          this[loadingFlag] = false
        })
    },
    // Scores of the tested product and of the comparables (GET /api/efficiencyTests/{id}/scores/export), the workbook
    // the execution kept.
    exportData() {
      if (!this.getExportPermission) return
      this.downloadWorkbook(
        '/api/efficiencyTests/' + this.scoringProfileId + '/scores/export',
        null,
        'isDownloading',
        FILE_NAME_EXPORT_EFFICIENCY_TEST
      )
    },
    generateReport() {
      if (!this.getExportPermission) return
      this.downloadWorkbook(
        '/api/efficiencyTests/' + this.scoringProfileId + '/report',
        { language: this.$i18n.locale },
        'isGeneratingReport',
        FILE_NAME_REPORT_EFFICIENCY_TEST
      )
    },
    resetFilters() {
      this.coverageFilters = INIT_COVERAGE_FILTERS_INSIGHTS()
      this.searchBar = ''
      const filters = {
        queryString: this.searchBar,
        ...this.coverageFilters,
      }
      this.filteredProducts(filters)
    },
    onApplyFilters(value) {
      this.coverageFilters = value
      const filters = {
        queryString: this.searchBar,
        ...this.coverageFilters,
      }
      this.filteredProducts(filters)
    },
    filterByString(value) {
      this.searchBar = value
      const filters = {
        queryString: this.searchBar,
        ...this.coverageFilters,
      }
      this.filteredProducts(filters)
    },
  },
  created() {
    this.loadingDetails = true
    this.loadingInsights = true

    this.$store
      .dispatch('efficiencyTestsCommon/loadScoringProfile', {
        id: this.scoringProfileId,
        action: '',
      })
      .catch((err) => {
        this.httpErrorsHandler(err)
      })
      .finally(() => {
        this.loadingDetails = false
      })

    this.loadInsight(this.scoringProfileId)
      .catch((err) => {
        this.httpErrorsHandler(err)
      })
      .finally(() => {
        this.loadingInsights = false
      })
  },
}
</script>

<style lang="scss" scoped>
.scoring-descriptions {
  color: var(--gray);
}
.row-loading {
  min-height: 150px;
}

.charts-stack {
  display: grid;
}
.charts-stack-item {
  grid-area: 1 / 1;
  min-width: 0;
  position: relative;
  z-index: 1;
}
// opacity too: Highcharts forces visibility="visible" on some elements (e.g. the legend's page arrows),
// which would otherwise show through over the visible chart.
.charts-stack-hidden {
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: 0;
}

.variant-orange {
  color: var(--primary);
}
</style>
